import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  RefreshCw, CheckCircle2, AlertTriangle, AlertCircle,
  Lock, LockOpen, XCircle, Calendar, Clock,
  ChevronRight,
} from 'lucide-react';
import { usePermission }    from '@/hooks/usePermission';
import { Permissions }      from '@/constants/permissions';
import { cn }               from '@/utils/cn';
import { monthEndApi }      from '@/api/monthEnd.api';
import { periodClosingApi } from '@/api/periodClosing.api';

function readError(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { message?: string; errors?: { message: string }[] } } };
  return err.response?.data?.errors?.[0]?.message ?? err.response?.data?.message ?? fallback;
}
import type { RootState }   from '@/state/store';
import type { MonthEndStatus, ModuleCloseStatus } from '@/api/monthEnd.api';

// ── Status colour helpers ───────────────────────────────────────────────────

const MODULE_STATUS_CHIP: Record<string, string> = {
  Open:       'bg-slate-100  text-slate-600  dark:bg-slate-700  dark:text-slate-300',
  SoftClosed: 'bg-blue-100   text-blue-700   dark:bg-blue-900/40 dark:text-blue-300',
  HardClosed: 'bg-amber-100  text-amber-700  dark:bg-amber-900/40 dark:text-amber-300',
  Locked:     'bg-red-100    text-red-700    dark:bg-red-900/40  dark:text-red-300',
};

const WORKFLOW_STATUS_CHIP: Record<string, string> = {
  None:      'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  Submitted: 'bg-sky-100   text-sky-700   dark:bg-sky-900/40 dark:text-sky-300',
  Approved:  'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Rejected:  'bg-red-100   text-red-700   dark:bg-red-900/40  dark:text-red-300',
};

// ── Helper: format date ──────────────────────────────────────────────────────
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Blocking item tile ───────────────────────────────────────────────────────
interface MetricTileProps {
  label: string;
  value: number;
  blocking?: boolean;
}
function MetricTile({ label, value, blocking = true }: MetricTileProps) {
  const isAlert = blocking && value > 0;
  return (
    <div className={cn(
      'rounded-xl border p-4 flex flex-col gap-1',
      isAlert
        ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
        : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800',
    )}>
      <span className={cn('text-2xl font-bold', isAlert ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-slate-100')}>
        {value}
      </span>
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
      {isAlert && <AlertCircle size={14} className="text-red-500 mt-1" />}
    </div>
  );
}

// ── Module row ─���─────────────────────────────────────────────────────────────
interface ModuleRowProps {
  mod: ModuleCloseStatus;
  onSoftClose: (entryId: string | null, module: string) => void;
  onHardClose: (entryId: string | null, module: string) => void;
  onLock:      (entryId: string | null, module: string) => void;
  onReopen:    (entryId: string | null, module: string) => void;
  canSoftClose: boolean;
  canHardClose: boolean;
  canLock:      boolean;
  canReopen:    boolean;
}
function ModuleRow({ mod, onSoftClose, onHardClose, onLock, onReopen,
    canSoftClose, canHardClose, canLock, canReopen }: ModuleRowProps) {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30">
      <td className="py-3 px-4 font-medium text-slate-700 dark:text-slate-200">{mod.module}</td>
      <td className="py-3 px-4">
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', MODULE_STATUS_CHIP[mod.status] ?? MODULE_STATUS_CHIP.Open)}>
          {mod.status}
        </span>
      </td>
      <td className="py-3 px-4">
        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', WORKFLOW_STATUS_CHIP[mod.workflowStatus] ?? WORKFLOW_STATUS_CHIP.None)}>
          {mod.workflowStatus}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400">{fmtDate(mod.closedDate)}</td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1">
          {mod.status === 'Open' && canSoftClose && (
            <button
              onClick={() => onSoftClose(mod.entryId, mod.module)}
              className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300"
            >
              Soft Close
            </button>
          )}
          {(mod.status === 'Open' || mod.status === 'SoftClosed') && canHardClose && (
            <button
              onClick={() => onHardClose(mod.entryId, mod.module)}
              className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300"
            >
              Hard Close
            </button>
          )}
          {mod.status === 'HardClosed' && canLock && (
            <button
              onClick={() => onLock(mod.entryId, mod.module)}
              className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300"
            >
              Lock
            </button>
          )}
          {mod.status !== 'Open' && mod.status !== 'Locked' && canReopen && (
            <button
              onClick={() => onReopen(mod.entryId, mod.module)}
              className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300"
            >
              Reopen
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export function MonthEndControlCenterPage() {
  const { hasPermission } = usePermission();

  // Get default financial year and period from session context
  const sessionFy     = useSelector((state: RootState) => state.session.session?.financialYearCode   ?? state.auth.user?.financialYearCode ?? '');
  const sessionPeriod = useSelector((state: RootState) => state.session.session?.financialPeriodCode ?? '');

  const [yearCode,   setYearCode]   = useState(sessionFy);
  const [periodCode, setPeriodCode] = useState(sessionPeriod);
  const [data,       setData]       = useState<MonthEndStatus | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [actionMsg,  setActionMsg]  = useState<string | null>(null);

  // ── Permissions ───────────────────────────────────────────────────────────
  const canView      = hasPermission(Permissions.MONTH_END_VIEW);
  const canInitiate  = hasPermission(Permissions.MONTH_END_INITIATE);
  const canApprove   = hasPermission(Permissions.MONTH_END_APPROVE);
  const canForce     = hasPermission(Permissions.MONTH_END_FORCE_CLOSE);
  const canSoftClose = hasPermission(Permissions.PERIOD_CLOSE_SOFT);
  const canHardClose = hasPermission(Permissions.PERIOD_CLOSE_HARD);
  const canLock      = hasPermission(Permissions.PERIOD_CLOSE_LOCK);
  const canReopen    = hasPermission(Permissions.PERIOD_CLOSE_REOPEN);

  // ── Load status ───────────────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    if (!yearCode.trim() || !periodCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await monthEndApi.getStatus(yearCode.trim(), periodCode.trim());
      setData(resp.data.data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load month-end status.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [yearCode, periodCode]);

  // Auto-load on mount if both codes are pre-filled from session
  useEffect(() => {
    if (sessionFy && sessionPeriod) {
      loadStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Module actions ────────────────────────────────────────────────────────
  async function handleClose(entryId: string | null, module: string, closeType: 'Soft' | 'Hard' | 'Locked') {
    if (!entryId) {
      setActionMsg(`No entry found for ${module}. Create one first.`);
      return;
    }
    try {
      await periodClosingApi.close(entryId, { closeType, remarks: null });
      setActionMsg(`${module} ${closeType} close applied.`);
      loadStatus();
    } catch {
      setActionMsg(`Failed to ${closeType.toLowerCase()} close ${module}.`);
    }
  }

  async function handleReopen(entryId: string | null, module: string) {
    if (!entryId) {
      setActionMsg(`No entry found for ${module}.`);
      return;
    }
    try {
      await periodClosingApi.reopen(entryId, { reason: 'Reopened from Month-End Control Center' });
      setActionMsg(`${module} reopened.`);
      loadStatus();
    } catch {
      setActionMsg(`Failed to reopen ${module}.`);
    }
  }

  /**
   * Starting and signing off the month.
   *
   * The endpoints, and the api wrappers for them, have always been here — the screen simply never
   * called them, so a month-end could be looked at but never begun. Initiating opens a closing
   * entry for each module, which is what every per-module button on this page needs in order to
   * do anything at all.
   */
  async function handleInitiate() {
    try {
      setActionMsg(null);
      const resp = await monthEndApi.initiate(yearCode.trim(), periodCode.trim());
      setData(resp.data.data);
      setActionMsg(`Month-end initiated for ${periodCode.trim()}.`);
    } catch (err) {
      setActionMsg(readError(err, 'Could not initiate the month-end.'));
    }
  }

  async function handleApprove() {
    try {
      setActionMsg(null);
      const resp = await monthEndApi.approve(yearCode.trim(), periodCode.trim());
      setData(resp.data.data);
      setActionMsg(`Month-end approved for ${periodCode.trim()}.`);
    } catch (err) {
      setActionMsg(readError(err, 'Could not approve the month-end.'));
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 dark:text-slate-400">
        You do not have permission to access the Month-End Control Center.
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Calendar size={22} className="text-slate-500 dark:text-gray-400" />
            Month-End Control Center
          </h1>
          {data && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {data.periodName} &bull; {fmtDate(data.periodStart)} – {fmtDate(data.periodEnd)}
            </p>
          )}
        </div>
        {data && (
          <div className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
            data.isReadyToClose
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
          )}>
            {data.isReadyToClose ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
            {data.isReadyToClose ? 'Ready to Close' : 'Blocked'}
          </div>
        )}
      </div>

      {/* ── Period selector ──────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">Period Selection</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 dark:text-slate-400">Financial Year</label>
            <input
              type="text"
              value={yearCode}
              onChange={e => setYearCode(e.target.value.toUpperCase())}
              placeholder="e.g. FY2026"
              className="border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm w-36
                         bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 dark:text-slate-400">Financial Period</label>
            <input
              type="text"
              value={periodCode}
              onChange={e => setPeriodCode(e.target.value.toUpperCase())}
              placeholder="e.g. 2026-07"
              className="border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm w-36
                         bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={loadStatus}
            disabled={loading || !yearCode.trim() || !periodCode.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700
                       text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading…' : 'Load Status'}
          </button>
          {canInitiate && (
            <button
              onClick={handleInitiate}
              disabled={loading || !yearCode.trim() || !periodCode.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700
                         text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Initiate Month-End
            </button>
          )}
          {canApprove && data?.isReadyToClose && (
            <button
              onClick={handleApprove}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700
                         text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Approve Month-End
            </button>
          )}
        </div>
      </div>

      {/* ── Error ────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-red-700 dark:text-red-300 text-sm">
          <XCircle size={15} /> {error}
        </div>
      )}

      {/* ── Action message ───────────────────────────────────────────────── */}
      {actionMsg && (
        <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 text-blue-700 dark:text-blue-300 text-sm">
          <CheckCircle2 size={15} /> {actionMsg}
          <button onClick={() => setActionMsg(null)} className="ml-auto text-xs underline">Dismiss</button>
        </div>
      )}

      {/* ── Skeleton ─────────────────────────────────────────────────────── */}
      {loading && !data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-20 bg-slate-100 dark:bg-slate-700 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {data && (
        <>
          {/* ── Close Readiness ──────────────────────────────────────────── */}
          <div className={cn(
            'rounded-xl border p-5',
            data.isReadyToClose
              ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
              : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20',
          )}>
            <div className="flex items-center gap-2 mb-2">
              {data.isReadyToClose
                ? <CheckCircle2 size={18} className="text-green-600 dark:text-green-400" />
                : <AlertTriangle size={18} className="text-red-600 dark:text-red-400" />}
              <h2 className={cn('font-semibold', data.isReadyToClose ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300')}>
                {data.isReadyToClose ? 'All clear — period is ready to close.' : 'Close is blocked by outstanding items.'}
              </h2>
            </div>
            {data.blockingReasons.length > 0 && (
              <ul className="space-y-1 mt-2">
                {data.blockingReasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                    <ChevronRight size={14} className="mt-0.5 flex-shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 flex items-center gap-1">
              <Clock size={12} /> As at {fmtDate(data.asAt)}
            </p>
          </div>

          {/* ── Blocking Items Grid ──────────────────────────────────────── */}
          <div>
            <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">Blocking Items</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <MetricTile label="Unposted Journals"           value={data.unpostedJournals} />
              <MetricTile label="Pending Approvals"           value={data.pendingApprovals} />
              <MetricTile label="Overdue Approvals"           value={data.overdueApprovals} />
              <MetricTile label="Unreconciled Banks"          value={data.unreconciledBankAccounts} />
              <MetricTile label="Unmatched Allocations"       value={data.unmatchedAllocations} />
              <MetricTile label="Failed Integration Batches"  value={data.failedIntegrationBatches} />
              <MetricTile label="Failed Posting Queue"        value={data.failedPostingQueueItems} />
              <MetricTile label="Unreconciled Card Settlements" value={data.unreconciledCardSettlements} />
            </div>
          </div>

          {/* ── Module Close Status ──────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
              <h2 className="font-semibold text-slate-700 dark:text-slate-200">Module Close Status</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 text-xs uppercase">
                    <th className="py-2 px-4 text-left">Module</th>
                    <th className="py-2 px-4 text-left">Status</th>
                    <th className="py-2 px-4 text-left">Workflow</th>
                    <th className="py-2 px-4 text-left">Closed Date</th>
                    <th className="py-2 px-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.moduleStatuses.map((mod) => (
                    <ModuleRow
                      key={mod.module}
                      mod={mod}
                      canSoftClose={canSoftClose}
                      canHardClose={canHardClose}
                      canLock={canLock}
                      canReopen={canReopen}
                      onSoftClose={(id, m) => handleClose(id, m, 'Soft')}
                      onHardClose={(id, m) => handleClose(id, m, 'Hard')}
                      onLock={(id, m)      => handleClose(id, m, 'Locked')}
                      onReopen={handleReopen}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Close Actions ──��─────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            <h2 className="font-semibold text-slate-700 dark:text-slate-200 mb-3">Close Actions</h2>
            <div className="flex flex-wrap gap-3">
              {canInitiate && (
                <button
                  disabled={!data.isReadyToClose}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700
                             text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  title={data.isReadyToClose ? 'Initiate month-end close' : 'Resolve blocking items first'}
                >
                  <Lock size={14} />
                  Initiate Close
                </button>
              )}
              {canReopen && (
                <button
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300
                             dark:bg-slate-700 dark:hover:bg-slate-600
                             text-slate-700 dark:text-slate-200 text-sm font-medium"
                >
                  <LockOpen size={14} />
                  Reopen Period
                </button>
              )}
              {canForce && (
                <button
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 bg-red-50
                             hover:bg-red-100 dark:bg-red-900/20 dark:border-red-700 dark:hover:bg-red-900/40
                             text-red-700 dark:text-red-300 text-sm font-medium"
                  title="Force-close all modules regardless of blocking items"
                >
                  <AlertTriangle size={14} />
                  Force Close All Modules
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────── */}
      {!loading && !data && !error && (
        <div className="flex flex-col items-center justify-center h-48 text-slate-400 dark:text-slate-500 gap-2">
          <Calendar size={36} strokeWidth={1.5} />
          <p className="text-sm">Enter a financial year and period code, then click Load Status.</p>
        </div>
      )}
    </div>
  );
}
